import { Inject } from '@odinjs/odin';
import { ButtonAPI, FieldAPI, FormAPI, InputAPI, ModalService, TextAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnChange, OnClick, OnMount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, MountEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';

import { Value } from '@philips-internal/oswald-ui-core';

import BaseController from '../../../BaseController';
import AdminMedicationScheduleReadingCard, { SearchAllMedicationsProps } from '../../services/admin-medication-schedule-card-reading-service';

import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { FragmentNames } from '../../../../../src/codegen';
import { AdministrationScheduleFractionalDoseAliases } from '../../../../../src/codegen/constants/aliases/administration-schedule-fractional-dose-aliases';
import AdminInfusionScheduleReadingCard, { SearchAllInfusionProps } from '../../services/admin-infusion-schedule-card-reading-service';
import MedicationService from '../../services/medication-service';
import { ContextDataProps, ReadCardProps, TextFieldsProps } from './administratio-schedule-fractional-dose-type';
import './administration-schedule-fractional-dose-controller.css';

@Controller({ fragment: FragmentNames.ADMINISTRATION_SCHEDULE_FRACTIONAL_DOSE })
class AdministrationScheduleFractionalDoseController extends BaseController {

  @Inject(AdministrationScheduleFractionalDoseAliases.ADM_SCHEDULE_FRACTIONAL_DOSE_FORM)
  private admScheduleFormApi!: FormAPI

  @Inject(AdministrationScheduleFractionalDoseAliases.ADM_SCHEDULE_FRACTIONAL_DOSE_NAME_TEXT)
  private nameInputTextApi!: TextAPI;

  @Inject(AdministrationScheduleFractionalDoseAliases.ADM_SCHEDULE_FRACTIONAL_DOSE_CODE_TEXT)
  private codeInputTextApi!: TextAPI;

  @Inject(AdministrationScheduleFractionalDoseAliases.ADM_SCHEDULE_FRACTIONAL_DOSE_REMAINING_TEXT)
  private remainingInputTextApi!: TextAPI;

  @Inject(AdministrationScheduleFractionalDoseAliases.ADM_SCHEDULE_FRACTIONAL_DOSE_REMAINING_FIELD)
  private remainingFieldApi!: TextAPI;

  @Inject(AdministrationScheduleFractionalDoseAliases.ADM_SCHEDULE_FRACTIONAL_UNIT_MEASURE_TEXT)
  private unitMeasureInputTextApi!: TextAPI;

  @Inject(AdministrationScheduleFractionalDoseAliases.ADM_SCHEDULE_FRACTIONAL_DOSE_BUTTON)
  private admSchedulebuttonApi!: ButtonAPI;

  @Inject(AdministrationScheduleFractionalDoseAliases.ADM_SCHEDULE_FRACTIONAL_DOSE_INPUT)
  private admScheduleInputApi!: InputAPI;

  @OnValidate(AdministrationScheduleFractionalDoseAliases.ADM_SCHEDULE_FRACTIONAL_DOSE_INPUT)
  onValidateSchedileFractionalDose(validation: ValidateEvent): void {
    const { data } = this.context as unknown as ContextDataProps;

    const formValues = this.admScheduleFormApi.getRecord();
    const remainingDoseInput = formValues.remainingDose;
    const remainingDose = Number(remainingDoseInput) ?? 0;
    const currentDoseQuantity = Number(data?.currentDoseQuantity)

    if (remainingDose < currentDoseQuantity) {
      validation.valid;
    }
  }

  formatRemainingTextField(doseQuantity: number): string {
    return `${MessageService.translate('text.remaining-quantity')} ${doseQuantity}`
  }

  validAndUpdatesDoseQuantity(responseBody: Record<string, Value>[]): void {
    const { remainingDoseQuantity, total, totalRead, message, messageType } = responseBody as unknown as ReadCardProps;
    const { data: { updatedCards, barcodeClose, unitMeasureDoseDescription } } = this.context as unknown as ContextDataProps;

    if (message) {
      MedicationService.showToast(String(message), this.toastType(String(messageType)));
    }

    if (total === totalRead) {
      updatedCards();
      ModalService.close('adm-schedule-fractional-dose');
      barcodeClose();
      return;
    }

    if (remainingDoseQuantity === 0) {
      updatedCards();
      ModalService.close('adm-schedule-fractional-dose');
      return;
    }

    MedicationService.showToast(this.formatRemainingTextField(remainingDoseQuantity), ToastLevel.WARNING);
    this.remainingFieldApi.setValue(String(remainingDoseQuantity));
    this.remainingInputTextApi?.setValue(`${remainingDoseQuantity} ${unitMeasureDoseDescription ?? ''}`);
    this.context.set({ currentDoseQuantity: remainingDoseQuantity });
  }

  readOneMedicationCard(remainingDoseInput: number): void {
    const context = this.context as unknown as ContextDataProps;

    const data = {
      barcode: context.data?.currentBarcode,
      doseQuantity: remainingDoseInput,
      prescriptionNumber: context.data?.prescriptionNumber,
      prescriptionType: context.data.prescriptionType,
      scheduleDate: String(context.data?.scheduleDate),
      scheduleSequence: Number(context.data?.scheduleSequenceNumber),
    }

    AdminMedicationScheduleReadingCard.readOneMedicationCard(data)
      .then((res) => {
        const responseBody = res as unknown as Record<string, Value>[]
        this.admScheduleInputApi.setValue('');
        this.validAndUpdatesDoseQuantity(responseBody);
      }).finally(() => {
        this.admSchedulebuttonApi.setEnabled(false)
        this.admScheduleFormApi.setLoading(false)
      })
  }

  readOneInfusionCard(remainingDoseInput: number): void {
    const context = this.context as unknown as ContextDataProps;

    const data = {
      barcode: context.data?.currentBarcode,
      doseQuantity: remainingDoseInput,
      prescriptionNumber: context.data?.prescriptionNumber,
      scheduleSequence: Number(context.data?.scheduleSequenceNumber),
      solutionSequenceNumber: Number(context.data?.solutionSequenceNumber),
      stepSolutionNumber: Number(context.data?.stepSolutionNumber),
    }

    AdminInfusionScheduleReadingCard.readOneInfusionCard(data)
      .then((res) => {
        const responseBody = res as unknown as Record<string, Value>[]

        this.validAndUpdatesDoseQuantity(responseBody);
      }).finally(() => {
        this.admSchedulebuttonApi.setEnabled(false)
        this.admScheduleFormApi.setLoading(false)
      })
  }

  insertedValueTextFields({ materialCode, materialDescription, itemQuantity }: TextFieldsProps): void {
    const { data } = this.context as unknown as ContextDataProps
    const unitMeasureDoseDescription = data.unitMeasureDoseDescription ?? ''
    this.nameInputTextApi?.setValue(String(materialDescription));
    this.codeInputTextApi?.setValue(String(materialCode));
    this.remainingInputTextApi?.setValue(`${itemQuantity} ${unitMeasureDoseDescription}`);
    this.admScheduleInputApi.setValue(itemQuantity);
  }

  searchAllMedicationsPendding(medicationParams: SearchAllMedicationsProps): void {
    AdminMedicationScheduleReadingCard.searchAllMedicationsPendding(medicationParams)
      .then((res) => {
        const responseBody = res as unknown as Record<string, Value>[]
        const { itemQuantity, materialCode, materialDescription } = responseBody
          .find((card) => Number(medicationParams.currentBarcode) === card.materialCode) as unknown as TextFieldsProps;

        this.context.set({ currentDoseQuantity: itemQuantity });
        this.insertedValueTextFields({ itemQuantity, materialCode, materialDescription });
      })
  }

  searchAllInfusionsPendding(infusionParams: SearchAllInfusionProps): void {
    AdminInfusionScheduleReadingCard.searchAllInfusionsPendding(infusionParams)
      .then((res) => {
        const responseBody = res as unknown as Record<string, Value>[]
        const { itemQuantity, materialCode, materialDescription } = responseBody
          .find((card) => Number(infusionParams.currentBarcode) === card.materialCode
            || Number(infusionParams.currentBarcode) === card.labelFractionatedNumber) as unknown as TextFieldsProps;

        this.context.set({ currentDoseQuantity: itemQuantity });
        this.insertedValueTextFields({
          itemQuantity,
          materialCode,
          materialDescription,
        });
      })
  }

  @OnChange({ target: AdministrationScheduleFractionalDoseAliases.ADM_SCHEDULE_FRACTIONAL_DOSE_INPUT })
  onChangeRemainingInput(event: ChangeEvent<InputAPI>): void {
    if (Number(event.value)) {
      this.admSchedulebuttonApi.setEnabled(true);
    } else {
      this.admSchedulebuttonApi.setEnabled(false);
    }
  }

  @OnMount({ target: AdministrationScheduleFractionalDoseAliases.ADM_SCHEDULE_FRACTIONAL_DOSE_CODE_TEXT })
  onMountAdminScheduleBarcode(): void {
    const { data } = this.context as unknown as ContextDataProps;

    const medicationParams: SearchAllMedicationsProps = {
      currentBarcode: data.currentBarcode,
      prescriptionNumber: data.prescriptionNumber,
      prescriptionType: data.prescriptionType,
      scheduleDate: String(data.scheduleDate),
      scheduleSequenceNumber: Number(data.scheduleSequenceNumber),
    }

    const infusionParams: SearchAllInfusionProps = {
      currentBarcode: data.currentBarcode,
      prescriptionNumber: data.prescriptionNumber,
      solutionSequenceNumber: Number(data.solutionSequenceNumber),
      stepSolutionNumber: Number(data.stepSolutionNumber)
    }

    if (data.medicationOrSolution === 'medication') {
      this.searchAllMedicationsPendding(medicationParams);
      return;
    }

    this.searchAllInfusionsPendding(infusionParams);
  }

  @OnMount(AdministrationScheduleFractionalDoseAliases.ADM_SCHEDULE_FRACTIONAL_UNIT_MEASURE)
  handleMountUnitMeasureField(event: MountEvent): void {
    const { data } = this.context as unknown as ContextDataProps
    const unitMeasureDose = data.unitMeasureDoseDescription
    const unitMeasureFieldApi = event.target as FieldAPI
    unitMeasureFieldApi.setVisible(!!unitMeasureDose)
  }

  @OnMount(AdministrationScheduleFractionalDoseAliases.ADM_SCHEDULE_FRACTIONAL_UNIT_MEASURE_TEXT)
  handleMountUnitMeasureText(event: MountEvent): void {
    const { data } = this.context as unknown as ContextDataProps
    const unitMeasureDose = data.unitMeasureDoseDescription
    const unitMeasureFieldApi = event.target as TextAPI
    unitMeasureFieldApi.setValue(String(unitMeasureDose))
    this.unitMeasureInputTextApi.setValue(String(unitMeasureDose))
  }

  @OnClick({ target: AdministrationScheduleFractionalDoseAliases.ADM_SCHEDULE_FRACTIONAL_DOSE_BUTTON })
  onClickButton(): void {
    const { data } = this.context as unknown as ContextDataProps;
    const formValues = this.admScheduleFormApi.getRecord();
    const remainingDoseInput = formValues.remainingDose;
    this.admScheduleFormApi.setLoading(true)
    this.admSchedulebuttonApi.setEnabled(false)
    if (data.medicationOrSolution === 'medication') {
      this.readOneMedicationCard(Number(remainingDoseInput));
      return;
    }

    this.readOneInfusionCard(Number(remainingDoseInput));
  }

  toastType(message: string): ToastLevel {
    switch (message) {
      case 'SUCCESS':
        return ToastLevel.SUCCESS;
      case 'ERROR':
        return ToastLevel.ERROR;
      default:
        return ToastLevel.SUCCESS;
    }
  }
}

export default AdministrationScheduleFractionalDoseController;
